import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Refresh interval: every 6 hours
const TOKEN_REFRESH_INTERVAL = 6 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const failedQueueRef = useRef([]);

  // Process queued requests after successful refresh
  const processQueue = (newToken, error) => {
    failedQueueRef.current.forEach(({ resolve, reject }) => {
      if (error) reject(error);
      else resolve(newToken);
    });
    failedQueueRef.current = [];
  };

  const doLogout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Attempt to refresh the token
  const attemptRefresh = useCallback(async (expiredToken) => {
    try {
      const response = await axios.post(`${API}/auth/refresh`, {
        token: expiredToken
      }, {
        // Skip the interceptor for refresh requests
        _skipInterceptor: true
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      if (userData) setUser(userData);
      return access_token;
    } catch {
      return null;
    }
  }, []);

  // Setup Axios response interceptor
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Skip interceptor for refresh endpoint itself or already retried
        if (originalRequest?._skipInterceptor || originalRequest?._retry) {
          return Promise.reject(error);
        }

        // Handle 401 errors (expired or invalid token)
        if (error.response?.status === 401 && !originalRequest._retry) {
          const currentToken = localStorage.getItem('token');
          
          if (!currentToken) {
            doLogout();
            return Promise.reject(error);
          }

          // If already refreshing, queue this request
          if (isRefreshingRef.current) {
            return new Promise((resolve, reject) => {
              failedQueueRef.current.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              originalRequest._retry = true;
              return axios(originalRequest);
            });
          }

          isRefreshingRef.current = true;
          originalRequest._retry = true;

          try {
            const newToken = await attemptRefresh(currentToken);
            if (newToken) {
              processQueue(newToken, null);
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            } else {
              processQueue(null, error);
              toast.error('Sessione scaduta. Effettua nuovamente il login.');
              doLogout();
              return Promise.reject(error);
            }
          } catch (refreshError) {
            processQueue(null, refreshError);
            toast.error('Sessione scaduta. Effettua nuovamente il login.');
            doLogout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshingRef.current = false;
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [attemptRefresh, doLogout]);

  // Setup periodic background refresh
  useEffect(() => {
    if (token && user) {
      // Refresh periodically to keep the session alive
      refreshTimerRef.current = setInterval(async () => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          await attemptRefresh(currentToken);
        }
      }, TOKEN_REFRESH_INTERVAL);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [token, user, attemptRefresh]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Don't immediately logout - the interceptor might refresh the token
      // Only logout if the interceptor also failed
      if (error.response?.status === 401) {
        const currentToken = localStorage.getItem('token');
        if (!currentToken) {
          doLogout();
        }
        // If token still exists, the interceptor is handling the refresh
      } else {
        console.error('Failed to fetch user:', error);
        doLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    doLogout();
  };

  const isAdmin = () => user?.role === 'admin';
  const isGestore = () => user?.role === 'gestore';
  const isAdminOrGestore = () => user?.role === 'admin' || user?.role === 'gestore';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAdmin, isGestore, isAdminOrGestore }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
